import { NgClass } from '@angular/common';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, DestroyRef, Input, OnChanges, SimpleChanges, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatTooltipModule } from '@angular/material/tooltip';
import { AuthService } from '../../../../auth/auth.service';
import { DashboardEngineService } from '../../../services/dashboard-engine.service';

export interface VmsCommunityRow {
  id: number;
  name: string;
  visitors_24h: number;
}

export interface VmsActivityData {
  total_visitors_24h: number;
  enabled_communities: number;
  communities: VmsCommunityRow[];
}

@Component({
  selector: 'app-vms-activity-card',
  standalone: true,
  imports: [NgClass, MatIconModule, MatProgressSpinnerModule, MatTooltipModule],
  templateUrl: './vms-activity-card.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class VmsActivityCardComponent implements OnChanges {
  private engine = inject(DashboardEngineService);
  private auth = inject(AuthService);
  private cdr = inject(ChangeDetectorRef);
  private destroyRef = inject(DestroyRef);

  @Input() dashboardType!: string;
  @Input() filters: any = {};
  @Input() requiredPermission?: string;

  data: VmsActivityData | null = null;
  loading = false;
  error: string | null = null;

  get hasPermission(): boolean {
    return !this.requiredPermission || this.auth.canAccess([this.requiredPermission]);
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (!this.hasPermission) return;
    if (changes['filters'] || changes['dashboardType']) {
      this.load();
    }
  }

  private load(): void {
    if (!this.dashboardType) return;
    this.loading = true;
    this.error = null;
    this.engine
      .getWidgetData(this.dashboardType, 'vmsActivity', this.filters)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (res: any) => {
          this.data = res.record?.data ?? res.record ?? null;
          this.loading = false;
          this.cdr.markForCheck();
        },
        error: (err: any) => {
          this.error = err.status === 403 ? 'Access restricted' : 'Unable to load visitor data';
          this.loading = false;
          this.cdr.markForCheck();
        },
      });
  }

  getVisitorBadgeClass(count: number): string {
    if (count === 0) return 'bg-gray-100 text-gray-400';
    if (count <= 10) return 'bg-blue-100 text-blue-700';
    if (count <= 50) return 'bg-indigo-100 text-indigo-700';
    return 'bg-violet-100 text-violet-700';
  }
}
